<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'
import type { ApiError } from '../types/auth.types'
import LanguageSelector from '../components/LanguageSelector.vue'

const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

// Form state
const email = ref('')
const password = ref('')
const rememberMe = ref(false)

// UI state
const loading = ref(false)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

/**
 * Handle login form submission
 */
const handleLogin = async () => {
  // Reset errors
  error.value = null
  validationErrors.value = {}
  loading.value = true

  try {
    // Get language from localStorage
    const selectedLanguage = localStorage.getItem('language') || 'pl'

    await authStore.login({
      email: email.value,
      password: password.value,
      language: selectedLanguage,
      remember_me: rememberMe.value
    })

    // Redirect based on user role
    const currentRole = authStore.initData?.persons?.current?.role

    if (currentRole === 'PLAYER') {
      router.push('/events')
    } else if (currentRole === 'OWNER' || currentRole === 'SUPERVISOR') {
      router.push('/dashboard')
    } else {
      // Default redirect for COACH or other roles
      router.push('/events')
    }
  } catch (err: any) {
    console.error('Login error:', err)

    if (err.response?.data) {
      const apiError = err.response.data as ApiError

      // Handle validation errors
      if (apiError.errors) {
        validationErrors.value = apiError.errors
      }

      // Handle general error message
      if (apiError.message) {
        error.value = apiError.message
      }
    } else {
      error.value = t('login.errorLogin')
    }
  } finally {
    loading.value = false
  }
}

/**
 * Get validation error for field
 */
const getFieldError = (field: string): string | null => {
  return validationErrors.value[field]?.[0] || null
}
</script>

<template>
  <main class="min-h-screen flex items-center justify-center px-4 py-8 bg-gray-50">
    <div class="w-full max-w-md">
      <!-- Language Selector -->
      <div class="mb-4 flex justify-end">
        <LanguageSelector />
      </div>

      <!-- Login Card -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-md p-4">
        <h2 class="text-xl font-semibold text-gray-800 mb-3">{{ t('login.title') }}</h2>
        <p class="text-sm text-gray-600 mb-4">{{ t('login.subtitle') }}</p>

        <!-- Error Alert -->
        <div v-if="error" class="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded">
          <p class="text-sm text-red-700">{{ error }}</p>
        </div>

        <form @submit.prevent="handleLogin" class="space-y-3">
          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ t('login.email') }}</label>
            <input
              type="email"
              id="email"
              v-model="email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getFieldError('email') }"
              :placeholder="t('login.emailPlaceholder')"
            />
            <p v-if="getFieldError('email')" class="mt-1 text-sm text-red-600">
              {{ getFieldError('email') }}
            </p>
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1">{{ t('login.password') }}</label>
            <input
              type="password"
              id="password"
              v-model="password"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getFieldError('password') }"
              :placeholder="t('login.passwordPlaceholder')"
            />
            <p v-if="getFieldError('password')" class="mt-1 text-sm text-red-600">
              {{ getFieldError('password') }}
            </p>
          </div>

          <!-- Remember Me -->
          <div class="flex items-center">
            <input
              type="checkbox"
              id="remember"
              v-model="rememberMe"
              class="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label for="remember" class="ml-2 text-sm text-gray-600">{{ t('login.rememberMe') }}</label>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? t('login.loggingIn') : t('login.loginButton') }}
          </button>
        </form>

        <!-- Additional Links -->
        <div class="mt-4 pt-3 border-t border-gray-200">
          <div class="flex flex-col gap-2 text-sm">
            <router-link to="/forgot-password" class="text-blue-600 hover:text-blue-700">
              {{ t('login.forgotPassword') }}
            </router-link>
            <p class="text-gray-600">
              {{ t('login.noAccount') }}
              <router-link to="/register" class="text-blue-600 hover:text-blue-700 font-medium">
                {{ t('login.register') }}
              </router-link>
            </p>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
